import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MediaLibrary from "@/pages/ph360/MediaLibrary";

// DOMPurify config for our final-editor content: allow images, headings,
// lists, blockquotes, basic inline formatting; block scripts / event handlers.
const SANITIZE_CFG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "b", "em", "i", "u", "s", "sub", "sup",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li", "blockquote",
    "figure", "figcaption", "img", "a",
    "table", "thead", "tbody", "tr", "th", "td",
    "section", "article", "aside", "div", "span",
    "hr", "code", "pre",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "id", "style", "target", "rel", "colspan", "rowspan"],
  ALLOW_DATA_ATTR: false,
};
const sanitize = (html) => DOMPurify.sanitize(html || "", SANITIZE_CFG);

export default function TabEditor({ project, onChange }) {
  const [autoHtml, setAutoHtml] = useState("");
  const [editorHtml, setEditorHtml] = useState(project.final_html || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/ph360/preview");
      setAutoHtml(data.html);
      if (!editorHtml) setEditorHtml(project.final_html || data.html);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg=null) => document.execCommand(cmd, false, arg);
  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/,"");
  const insertHTML = (html) => { editorRef.current?.focus(); document.execCommand("insertHTML", false, html); };
  const insertImageUrl = () => {
    const url = window.prompt("Paste an image URL (https://…):");
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) { toast.error("Enter a full URL starting with http(s)://"); return; }
    insertHTML(`<figure class="ef360-imported-image-wrap"><img src="${url}" alt="" style="max-width:100%"/></figure>`);
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ph360/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const abs = data.url.startsWith("http") ? data.url : `${backendBase}${data.url}`;
      insertHTML(`<figure class="ef360-imported-image-wrap"><img src="${abs}" alt="" style="max-width:100%"/></figure>`);
      toast.success("Image inserted");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const save = async () => {
    setBusy(true);
    try {
      // Sanitize on the way OUT as well as on render — prevents storing raw
      // HTML if a drag-and-drop paste bypasses execCommand.
      const html = sanitize(editorRef.current?.innerHTML || editorHtml);
      const { data } = await api.post("/ph360/final-editor/save", { final_html: html });
      onChange(data);
      toast.success("Final manuscript saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset back to the auto-formatted version?")) return;
    try {
      let fresh = autoHtml;
      if (!fresh) {
        const { data } = await api.get("/ph360/preview");
        fresh = data.html;
        setAutoHtml(fresh);
      }
      const { data } = await api.post("/ph360/final-editor/reset");
      onChange(data);
      setEditorHtml(fresh);
      if (editorRef.current) editorRef.current.innerHTML = sanitize(fresh);
      toast.success("Reset to auto-formatted version");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!project.structured?.chapters?.length && !project.final_html) {
    return <p>No project yet. Import content first.</p>;
  }
  return (
    <>
      <h2>Final Editor / Last-Mile Polish</h2>
      <p>Edit the final manuscript after auto-formatting. Use this for tiny spacing fixes, wording polish, and final review before PDF/DOCX export.</p>
      {project.use_final_html
        ? <div className="notice notice-success"><p><strong>Final Editor is active.</strong> Exports will use the saved edited manuscript.</p></div>
        : <div className="notice notice-warning"><p><strong>Final Editor is not active yet.</strong> Click "Save Final Manuscript" to make exports use this edited version.</p></div>}
      <div className="ef360-editor-toolbar" data-testid="editor-toolbar">
        {[
          ["B","bold"],["I","italic"],["U","underline"],
          ["H1","formatBlock","h1"],["H2","formatBlock","h2"],["P","formatBlock","p"],
          ["• List","insertUnorderedList"],["1. List","insertOrderedList"],
          ["❝","formatBlock","blockquote"],
          ["← Undo","undo"],["→ Redo","redo"],
        ].map(([label, cmd, arg]) => (
          <button key={label} onClick={(e)=>{e.preventDefault(); exec(cmd, arg);}} data-testid={`editor-btn-${label}`}>{label}</button>
        ))}
        <button onClick={(e)=>{e.preventDefault(); insertImageUrl();}} data-testid="editor-btn-img-url">🖼 URL</button>
        <button onClick={(e)=>{e.preventDefault(); fileInputRef.current?.click();}} disabled={uploading} data-testid="editor-btn-img-upload">
          {uploading ? "Uploading…" : "⬆ Upload"}
        </button>
        <button onClick={(e)=>{e.preventDefault(); setShowLibrary(true);}} data-testid="editor-btn-library">📚 Library</button>
        <input ref={fileInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={onUpload} data-testid="editor-file-input"/>
      </div>
      {showLibrary && (
        <MediaLibrary
          onClose={()=>setShowLibrary(false)}
          onInsert={(url)=>insertHTML(`<figure class="ef360-imported-image-wrap"><img src="${url}" alt="" style="max-width:100%"/></figure>`)}
        />
      )}
      <div
        ref={editorRef}
        className="ef360-final-editor"
        contentEditable
        suppressContentEditableWarning
        dangerouslySetInnerHTML={{ __html: sanitize(editorHtml) }}
        data-testid="ph360-final-editor"
      />
      <p style={{marginTop:14}}>
        <button className="button button-primary" onClick={save} disabled={busy} data-testid="editor-save">
          {busy ? "Saving…" : "Save Final Manuscript"}
        </button>{" "}
        <button className="button" onClick={reset} data-testid="editor-reset">Reset to Auto-Formatted Version</button>
      </p>
    </>
  );
}
